/* eslint-disable no-nested-ternary */
import {
  Card,
  CardContent,
  CardHeader,
  Chip,
  FormControl,
  Skeleton,
  Stack,
  TextField,
} from '@mui/material';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormHelperText from '@mui/material/FormHelperText';
import FormLabel from '@mui/material/FormLabel';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import makeStyles from '@mui/styles/makeStyles';
import PropTypes from 'prop-types';

import Grid from '@mui/material/Grid';
import TagsAutocomplete from '../../../../../../../components/Autocompletes/Tags';

import styles from './styles';

const useStyles = makeStyles(styles);

export const GeneralFormComponent = (props) => {
  const classes = useStyles();
  const {
    eventTemplate,
    barn,
    handleNameChange,
    handleChangeTags,
    handleDescriptionChange,
    isLoading,
    tags,
    allTags,
  } = props;

  // Find the horse or barn task id in event template tags, set value if present
  const taskTypeTag = (tags || [])
    .find((t) => t.id === barn.barnMetadata.horseTaskTypeTagId || t.id === barn.barnMetadata.barnTaskTypeTagId) || { id: null };
  const isHorseTask = taskTypeTag.id === barn.barnMetadata.horseTaskTypeTagId;
  const taskTypeLabel
    = isHorseTask
      ? 'Horse Task'
      : taskTypeTag.id
        ? 'Barn/Location Task'
        : 'Task Type';
  const taskTypeDescription = isHorseTask
    ? 'Horse tasks appear when creating or scheduling a task for a horse (shoeing, vaccines, grooming, etc.).'
    : 'Barn tasks appear when creating or scheduling a task for a location (chores, feed prep, drag ring, etc.).';

  const handleChangeTaskType = (e) => {
    handleChangeTags(
      [
        ...tags.filter((t) => (t.tagType.id === barn.barnMetadata.tagsTagTypeId || t.tagType.id === barn.barnMetadata.healthTagTypeId)),
        allTags.find((t) => t.id.toString() === e.target.value)],
    );
  };

  if (isLoading) {
    return (
      <Card className={classes.card}>
        <CardHeader
          title="General Task Details"
          subheader="Define a task by giving it a name, description, and task type."
        />
        <CardContent>
          <Skeleton height={56} sx={{ mb: 2 }} />
          <Skeleton height={56} sx={{ mb: 2 }} />
          <Skeleton height={56} />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={classes.card}>
      <CardHeader
        sx={{ mb: 2 }}
        title="General Task Details"
        subheader="Define a task by giving it a name, description, and task type."
      />
      <CardContent>
        <Grid container spacing={3}>
          <Grid
            size={{
              xs: 12,
              md: 6,
            }}
          >
            <TextField
              error={!eventTemplate.validation.name.isValid}
              fullWidth
              required
              variant="outlined"
              id="name"
              label="Name"
              value={eventTemplate.name}
              autoFocus
              onChange={handleNameChange}
              className={classes.nameField}
              helperText={
                !eventTemplate.validation.name.isValid
                && eventTemplate.validation.name.message
              }
            />
            {
              barn.barnMetadata && (
                <FormControl
                  margin="normal"
                  fullWidth
                  variant="outlined"
                >
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                    <FormLabel id="task-type-radio-buttons-group-label">Task Type</FormLabel>
                    {taskTypeTag.id && (
                      <Chip
                        size="small"
                        color={isHorseTask
                          ? 'primary'
                          : 'secondary'}
                        variant="outlined"
                        label={taskTypeLabel}
                      />
                    )}
                  </Stack>
                  <RadioGroup
                    row
                    aria-labelledby="task-type-radio-buttons-group-label"
                    name="task-type-radio-buttons-group"
                    onChange={handleChangeTaskType}
                    value={taskTypeTag.id}
                  >
                    <FormControlLabel value={barn.barnMetadata.horseTaskTypeTagId} control={<Radio />} label="Horse" sx={{ mb: 0 }} />
                    <FormControlLabel value={barn.barnMetadata.barnTaskTypeTagId} control={<Radio />} label="Barn/Location (chores)" sx={{ mb: 0 }} />
                  </RadioGroup>

                  {taskTypeTag.id && (
                    <FormHelperText sx={{ ml: 0 }}>
                      {taskTypeDescription}
                    </FormHelperText>
                  )}
                </FormControl>
              )
            }
          </Grid>
          <Grid
            size={{
              xs: 12,
              md: 6,
            }}
          >
            <TextField
              error={!eventTemplate.validation.description.isValid}
              fullWidth
              variant="outlined"
              id="description"
              label="Description"
              value={eventTemplate.description}
              onChange={handleDescriptionChange}
              multiline
              rows={4}
              helperText={
                !eventTemplate.validation.description.isValid
                && eventTemplate.validation.description.message
              }
            />
          </Grid>

          <Grid size={12}>
            <Stack spacing={1}>
              <TagsAutocomplete
                onSelect={(e, value) => { handleChangeTags(value); }}
                value={tags}
                size="default"
              />
              <FormHelperText>
                Use tags to group together similar tasks or to denote distinctions and/or categories.
              </FormHelperText>
            </Stack>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};

GeneralFormComponent.propTypes = {
  eventTemplate: PropTypes.object,
  barn: PropTypes.object,
  tags: PropTypes.array,
  allTags: PropTypes.array,
  handleNameChange: PropTypes.func,
  handleChangeTags: PropTypes.func,
  handleDescriptionChange: PropTypes.func,
  isLoading: PropTypes.bool,
};

GeneralFormComponent.defaultProps = {
  eventTemplate: {},
  barn: { barnMetadata: {} },
  tags: [],
  allTags: [],
  handleNameChange: () => { },
  handleChangeTags: () => { },
  handleDescriptionChange: () => { },
  isLoading: true,
};

export default GeneralFormComponent;
